import React from 'react';
import { Link } from 'react-router-dom';
import { canonicalFor, usePageMeta } from '../../hooks/usePageMeta';
import { PageHeader } from '../../components/page/PageHeader';
import { ContentSection } from '../../components/page/ContentSection';
import { CatalogTable } from '../../components/page/CatalogTable';
import { SalesCallout } from '../../components/page/SalesCallout';
import { RelatedLinks } from '../../components/page/RelatedLinks';
import { catalog } from '../../data/catalog';
const setupChecks = ['Tool clearance and holder orientation in the turret', 'Tool overhang and setup rigidity', 'Guarding and chip evacuation', 'Machine horsepower and limits'];
export function ToolHolders() {
  usePageMeta({
    title: 'SALXR 16-3D & SALXR 12-3B Tool Holders | Robertson Precision',
    description: 'Shear Geometry external holders: SALXR 16-3D with a 1-inch shank and SALXR 12-3B with a 3/4-inch shank, each with a Torx driver. For CNC lathes only.',
    canonical: canonicalFor('/products/holders/')
  });
  return <>
      <PageHeader eyebrow="Products" title="Tool Holders" lead="Two external turning holders, one for each catalog shank size. Each holder is supplied with a Torx driver and takes replaceable APGX inserts." crumbs={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Products',
      href: '/products/'
    }, {
      label: 'Tool Holders'
    }]} data-id="element-833" data-mp="6wdounwcinkq" />

      <ContentSection id="holders" title="Holders" data-id="element-834" data-mp="3k95s3080h3py">
        <CatalogTable rows={catalog.filter(c => c.category === 'holders')} caption="Shear Geometry tool holders" data-id="element-835" data-mp="3p1guqau4z3m7" />
      </ContentSection>

      <ContentSection id="choosing" tone="warm" title="Choosing and mounting a holder" data-id="element-836" data-mp="3fbdt5m8y8hc9">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16" data-id="element-837" data-mp="3f91cdcexgd0s">
          <div className="rp-measure space-y-4 text-[15.5px] leading-[1.7] text-graphite/85" data-id="element-838" data-mp="31cxlnt39imt2">
            <p data-id="element-839" data-mp="369ebcrq41989">
              Match the holder shank to the tool station on your turret: SALXR
              16-3D for 1-inch, SALXR 12-3B for 3/4-inch. Then choose the{' '}
              <Link to="/products/inserts/" className="text-navy underline decoration-copper underline-offset-4 hover:text-copper" data-id="element-840" data-mp="17391tpe2xqwn">
                insert
              </Link>{' '}
              for the material.
            </p>
            <p data-id="element-841" data-mp="1utczlg4l6wfq">
              Inserts are secured with an insert screw. The catalog’s service
              parts are the T-15 Torx driver and the 4-15 low-profile insert
              screw — see{' '}
              <Link to="/products/accessories/" className="text-navy underline decoration-copper underline-offset-4 hover:text-copper" data-id="element-842" data-mp="1lsnbkylkiack">
                Accessories
              </Link>
              , and have sales confirm the correct parts for your holder.
            </p>
            <p className="font-mono text-[12px] tracking-[0.05em] text-graphite" data-id="element-843" data-mp="5e5iizvcwmlm">
              For CNC lathes only. Not for manual lathes.
            </p>
          </div>
          <div data-id="element-844" data-mp="2m0v2pnuab96e">
            <h3 className="font-display text-[19px] font-medium text-navy" data-id="element-845" data-mp="2m0bvb129g36m">
              Check before the first cut
            </h3>
            <ul className="mt-4 divide-y divide-rule border-y border-rule" data-id="element-846" data-mp="2q1neooujjs6d">
              {setupChecks.map(item => <li key={item} className="flex gap-3 py-3 text-[15px] text-graphite" data-id="element-847" data-mp="2uugklg1qx6k1">
                  <span aria-hidden="true" className="mt-2.5 h-px w-3 shrink-0 bg-copper" data-id="element-848" data-mp="2zrqd54kflyc3" />
                  {item}
                </li>)}
            </ul>
            <Link to="/technical-resources/safety/" className="mt-5 inline-flex min-h-[44px] items-center border-b border-copper text-[14px] font-semibold text-navy hover:text-copper" data-id="element-849" data-mp="39q67rhio3pqc">
              Workholding &amp; Safety
            </Link>
          </div>
        </div>
        <div className="mt-10" data-id="element-850" data-mp="1jertqdldfndy">
          <SalesCallout location="holders" data-id="element-851" data-mp="1jg2yogqjz16n" />
        </div>
      </ContentSection>

      <RelatedLinks links={[{
      label: 'Inserts & Chipbreakers',
      href: '/products/inserts/',
      description: 'Seven APGX inserts in three families.'
    }, {
      label: 'Accessories',
      href: '/products/accessories/',
      description: 'Driver and insert screw for service.'
    }, {
      label: 'Starter Kits',
      href: '/products/starter-kits/',
      description: 'Holder, inserts and driver together.'
    }, {
      label: 'Workholding & Safety',
      href: '/technical-resources/safety/',
      description: 'Setup checks and the workholding chart.'
    }]} data-id="element-852" data-mp="o28ufxlpxkzj" />
    </>;
}